import React, { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Plus,
  Check,
  Star,
  Globe2,
  Calendar,
  MapPin,
  Smartphone,
  Monitor,
  Users,
  ShieldCheck,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useCompare } from "@/context/CompareContext";
import { useSocialMeta } from "@/hooks/useSocialMeta";
import { getApp, getFeatureTaxonomy, goUrl, getSimilarPricedApps, mediaUrl } from "@/lib/api";
import { formatPlanPrice, planEffectiveMonthly, planModel } from "@/lib/pricing";
import { planApproximations } from "@/lib/fx";
import { useFxRates } from "@/hooks/useFxRates";
import AdSlot from "@/components/AdSlot";
import AppGallery from "@/components/AppGallery";
import AppReviews from "@/components/AppReviews";
import FeatureSpotlight from "@/components/FeatureSpotlight";
import JsonLd from "@/components/JsonLd";
import NewBadge from "@/components/NewBadge";
import UpdatedBadge from "@/components/UpdatedBadge";
import PublisherNudge from "@/components/PublisherNudge";
import VerifiedBadge from "@/components/VerifiedBadge";
import AppLogo from "@/components/AppLogo";
import { MarkdownView } from "@/components/MarkdownEditor";
import { APP_DETAIL } from "@/constants/testIds";

const PROD_ORIGIN = "https://horseappcompare.com";


/**
 * Sidebar price widget (top-right).
 *
 * Used to read the legacy `app.pricing.monthly_usd` field (which is almost
 * always 0 nowadays because publishers manage tiers via `app.plans`).
 * Result: the sidebar said "$0" while the pricing-tier section underneath
 * showed real prices like "£2/horse/mo". This component reconciles the
 * two:
 *   1. If `app.plans` has paid entries → show the cheapest one ("From €19/mo")
 *      with FX approximations underneath.
 *   2. If all plans are free → "Free".
 *   3. If no plans → fall back to the legacy USD fields so older listings
 *      that haven't been claimed yet keep working.
 */
const SidebarPrice = ({ app, lang, fxRates, freeLabel, fromLabel }) => {
  const plans = Array.isArray(app?.plans) ? app.plans : [];
  const paid = plans.filter((p) => planModel(p) !== "free" && planEffectiveMonthly(p) > 0);

  // 1. Plans-array path
  if (plans.length > 0) {
    if (paid.length === 0) {
      return <div className="font-heading text-3xl text-[#0F172A]">{freeLabel}</div>;
    }
    const cheapest = [...paid].sort((a, b) => planEffectiveMonthly(a) - planEffectiveMonthly(b))[0];
    const { primary, secondary } = formatPlanPrice(cheapest, lang);
    const approx = planApproximations(cheapest, fxRates, lang);
    return (
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#64748B] mb-0.5">{fromLabel}</div>
        <div className="font-heading text-2xl text-[#0F172A] leading-tight break-words">{primary}</div>
        {secondary && (
          <div className="text-xs text-[#64748B] mt-0.5 font-mono">{secondary}</div>
        )}
        {approx.length > 0 && (
          <div
            className="text-[11px] text-[#94A3B8] mt-1 font-mono"
            title={lang === "nl" ? "Indicatieve omrekening — koers via ECB" : "Approximate — daily ECB rate"}
            data-testid="sidebar-price-approx"
          >
            {approx.join(" · ")}
          </div>
        )}
        {cheapest.name && (
          <div className="text-[11px] text-[#94A3B8] mt-1.5">{lang === "nl" ? "Goedkoopste plan:" : "Cheapest plan:"} <span className="text-[#475569] font-medium">{cheapest.name}</span></div>
        )}
      </div>
    );
  }

  // 2. Legacy fallback
  const monthly = Number(app?.pricing?.monthly_usd) || 0;
  const yearly = Number(app?.pricing?.yearly_usd) || 0;
  if (monthly === 0 && yearly === 0) {
    return <div className="font-heading text-3xl text-[#0F172A]">{freeLabel}</div>;
  }
  const moSuffix = lang === "nl" ? "/mnd" : "/mo";
  const yLbl = lang === "nl" ? "/jaar" : "/year";
  return (
    <div>
      <div className="font-heading text-3xl text-[#0F172A]">${monthly}<span className="text-base text-[#64748B]">{moSuffix}</span></div>
      {yearly > 0 && (
        <div className="text-xs text-[#64748B] mt-1 font-mono">${yearly}{yLbl}</div>
      )}
    </div>
  );
};


/**
 * Similar-priced apps strip — small CTA below the sidebar pricing widget.
 *
 * Calls `/api/apps/{slug}/similar-priced` which buckets apps into 5 price
 * bands (free / <€10 / €10-25 / €25-75 / €75+) within the same category.
 * Each chip is a direct link to `/compare/{slug}-vs-{rival}` so we route
 * traffic to the high-converting versus-pages.
 */
const SimilarPricedStrip = ({ slug, lang }) => {
  const isNl = lang === "nl";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSimilarPricedApps(slug, 3)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading || !data || !data.items?.length) return null;

  const label = isNl ? data.band_label_nl : data.band_label_en;

  return (
    <div className="border-t border-[#E2E8F0] pt-4" data-testid="similar-priced-strip">
      <div className="overline mb-2">{isNl ? "Vergelijk prijsgenoten" : "Compare same price range"}</div>
      <div className="text-[11px] text-[#94A3B8] mb-3">
        {isNl
          ? <>{data.items.length} {data.items.length === 1 ? "app" : "apps"} in <strong className="text-[#475569]">{label}</strong></>
          : <>{data.items.length} {data.items.length === 1 ? "app" : "apps"} in <strong className="text-[#475569]">{label}</strong></>}
      </div>
      <ul className="space-y-2">
        {data.items.map((it) => (
          <li key={it.slug}>
            <Link
              to={`/compare/${slug}-vs-${it.slug}`}
              className="group flex items-center gap-2 bg-[#F8FAFC] hover:bg-[#EFF6FF] border border-[#E2E8F0] hover:border-[#2563EB] rounded-lg p-2 transition-colors"
              data-testid={`similar-priced-${it.slug}`}
            >
              {it.logo ? (
                <img src={mediaUrl(it.logo)} alt="" className="w-8 h-8 rounded object-cover shrink-0 bg-white border border-[#E2E8F0]" />
              ) : (
                <div className="w-8 h-8 rounded bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center shrink-0">
                  {(it.name || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-[#0F172A] truncate group-hover:text-[#2563EB]">{it.name}</div>
                {it.eur_monthly > 0 && (
                  <div className="text-[10px] text-[#94A3B8] font-mono">≈ €{it.eur_monthly < 10 ? it.eur_monthly.toFixed(1).replace(/\.0$/, "") : Math.round(it.eur_monthly)}/mo</div>
                )}
              </div>
              <ArrowRight size={12} className="text-[#94A3B8] group-hover:text-[#2563EB] shrink-0" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};


const AppDetailPage = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const { slugs, toggle } = useCompare();
  const location = useLocation();
  const [app, setApp] = useState(null);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const fxRates = useFxRates();

  useEffect(() => {
    setLoading(true);
    Promise.all([getApp(slug), getFeatureTaxonomy()])
      .then(([a, taxonomy]) => {
        setApp(a);
        setCategories(taxonomy.categories || {});
      })
      .catch(() => setApp(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Smooth-scroll to anchor (e.g. #plans) once the app data has rendered.
  useEffect(() => {
    if (loading || !app || !location.hash) return;
    const id = location.hash.replace(/^#/, "");
    const el = document.getElementById(id);
    if (el) {
      // Defer one frame so layout has settled.
      requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [loading, app, location.hash]);

  const isNl = lang === "nl";
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  // Compute lang-aware fields BEFORE useSocialMeta so localized pages have
  // their own unique meta description (anti-thin-content for hreflang variants).
  const tagline = app && (lang === "nl" && app.tagline_nl ? app.tagline_nl : app.tagline);
  const description = app && (lang === "nl" && app.description_nl ? app.description_nl : app.description);
  useSocialMeta(app ? {
    title: `${app.name} ${isNl ? "review, prijzen & alternatieven" : "review, pricing & alternatives"} | HorseAppCompare`,
    description: tagline || (isNl
      ? `Review van ${app.name}: functies, prijzen, doelgroepen en de beste alternatieven.`
      : `Review of ${app.name}: features, pricing, target audiences and the best alternatives.`),
    image: `${BACKEND}/api/og/app/${app.slug}.png`,
    type: "website",
  } : { title: "HorseAppCompare", description: t.hero?.lede });

  if (loading) {
    return <main className="hac-container py-20 text-center text-[#94A3B8]">…</main>;
  }
  if (!app) {
    return (
      <main className="hac-container py-20 text-center">
        <p className="text-[#94A3B8] mb-4">{t.common.noResults}</p>
        <Link to="/apps" className="hac-btn-secondary">
          <ArrowLeft size={14} /> {t.common.backToDirectory}
        </Link>
      </main>
    );
  }

  const inCompare = slugs.includes(app.slug);
  const featureSet = new Set(app.features || []);

  // ---- Structured data for Google rich results ------------------------------
  const cheapestPlan = (app.plans || []).slice().sort((a, b) => (a.monthly || 0) - (b.monthly || 0))[0];
  const monthlyUsd = app.pricing?.monthly_usd;
  const platformsList = Array.isArray(app.platforms)
    ? app.platforms
    : Object.entries(app.platforms || {}).filter(([, v]) => v).map(([k]) => k.charAt(0).toUpperCase() + k.slice(1));
  const softwareAppLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: app.name,
    description: description || tagline,
    applicationCategory: "LifestyleApplication",
    operatingSystem: platformsList.join(", ") || "Web, iOS, Android",
    url: `${PROD_ORIGIN}/apps/${app.slug}`,
    // Use the dynamic OG card (1200×630 PNG) as the structured-data image.
    // It is server-rendered from the live app data (logo, name, score) and
    // always returns 200 — unlike legacy `app.logo` Clearbit URLs which
    // Clearbit stopped serving in late 2024. This is the image Google can
    // surface in rich results.
    image: `${PROD_ORIGIN}/api/og/app/${app.slug}.png`,
    ...(app.logo && { logo: app.logo.startsWith("http") ? app.logo : `${PROD_ORIGIN}${app.logo}` }),
    ...((app.review_count || app.rating) && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(app.rating || 4.5).toFixed(1),
        reviewCount: Math.max(1, Number(app.review_count || 1)),
        bestRating: "5",
        worstRating: "1",
      },
    }),
    ...((cheapestPlan || monthlyUsd) && {
      offers: {
        "@type": "Offer",
        price: cheapestPlan ? Number(cheapestPlan.monthly || 0).toFixed(2) : Number(monthlyUsd || 0).toFixed(2),
        priceCurrency: cheapestPlan?.currency || "USD",
        availability: "https://schema.org/InStock",
      },
    }),
    // Google freshness signals — listing edits bump `updated_at` server-side
    // and (when claimed) the publisher is the editor → dateModified reflects
    // *real* publisher activity, not just our internal seed dates.
    ...(app.updated_at && { dateModified: app.updated_at }),
    ...(app.created_at && { datePublished: app.created_at }),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${PROD_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: isNl ? "Apps" : "Directory", item: `${PROD_ORIGIN}/apps` },
      { "@type": "ListItem", position: 3, name: app.name, item: `${PROD_ORIGIN}/apps/${app.slug}` },
    ],
  };

  return (
    <main>
      <JsonLd id="app-software" data={softwareAppLd} />
      <JsonLd id="app-breadcrumb" data={breadcrumbLd} />
      <section
        className="relative border-b border-[#E2E8F0]"
        style={{ background: `linear-gradient(180deg, ${app.color}15 0%, transparent 100%)` }}
      >
        <div className="hac-container py-12 md:py-16">
          {app.verified_owner_email && <PublisherNudge app={app} isNl={isNl} />}
          <Link
            to="/apps"
            className="text-xs text-[#64748B] hover:text-[#0F172A] inline-flex items-center gap-1 mb-8 transition-colors"
          >
            <ArrowLeft size={12} /> {t.common.backToDirectory}
          </Link>

          <div className="flex flex-col md:flex-row items-start gap-8">
            <AppLogo app={app} className="w-20 h-20 md:w-28 md:h-28 rounded-2xl" textClassName="text-4xl" />
            <div className="flex-1">
              <div className="overline mb-2">{app.country}</div>
              {app.recently_listed && <NewBadge app={app} variant="strip" />}
              {!app.recently_listed && app.recently_updated && <UpdatedBadge app={app} variant="strip" />}
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <VerifiedBadge app={app} size="md" showLabel />
              </div>
              <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-tight mb-3">
                {app.name}
              </h1>
              <p className="text-lg text-[#64748B] leading-relaxed max-w-2xl mb-5">{tagline}</p>
              {app.verified_owner_email ? (
                <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider mb-4" data-testid="app-verified-badge">
                  <ShieldCheck size={12} /> {isNl ? "Geverifieerde eigenaar" : "Verified owner"}
                </div>
              ) : (
                <Link to={`/publisher/claim/${app.slug}`} className="inline-flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 px-3 py-1.5 rounded-full text-xs font-medium transition-colors mb-4" data-testid="app-claim-link">
                  {isNl ? "Is dit jouw app? Claim het →" : "Is this your app? Claim it →"}
                </Link>
              )}
              {app.verified_owner_email && app.recently_updated && app.updated_at && (
                <div
                  className="inline-flex items-center gap-1.5 text-[#64748B] text-xs ml-2 mb-4"
                  data-testid="publisher-heartbeat"
                  title={isNl
                    ? "De geverifieerde eigenaar van deze app heeft de listing recent bijgewerkt."
                    : "The verified owner of this app refreshed the listing recently."}
                >
                  <RefreshCw size={11} className="text-sky-500" />
                  <span>
                    {isNl ? "Bijgewerkt door eigenaar" : "Refreshed by publisher"} ·{" "}
                  </span>
                  <time dateTime={app.updated_at} className="font-medium text-[#0F172A]">
                    {(() => {
                      const days = app.updated_days_ago ?? 0;
                      if (days <= 0) return isNl ? "vandaag" : "today";
                      if (days === 1) return isNl ? "gisteren" : "yesterday";
                      return isNl ? `${days} dagen geleden` : `${days} days ago`;
                    })()}
                  </time>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#64748B]">
                <span className="inline-flex items-center gap-1">
                  <Star size={14} className="fill-[#2563EB] text-[#2563EB]" />
                  <span className="font-mono">{app.rating?.toFixed(1)}</span>
                  <span className="text-[#94A3B8]">
                    ({app.review_count} {t.common.reviews})
                  </span>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Calendar size={14} /> {app.founded}
                </span>
                <span className="inline-flex items-center gap-1">
                  <MapPin size={14} /> {app.regions?.join(", ")}
                </span>
              </div>

              <div className="flex flex-wrap gap-3 mt-7">
                <a
                  href={goUrl(app.slug, "detail")}
                  target="_blank"
                  rel="noopener sponsored nofollow noreferrer"
                  data-testid={APP_DETAIL.visit}
                  className="hac-btn-primary"
                >
                  <ExternalLink size={14} /> {t.common.visit}
                </a>
                <button
                  type="button"
                  onClick={() => toggle(app.slug)}
                  data-testid={APP_DETAIL.addCompare}
                  className="hac-btn-secondary"
                >
                  {inCompare ? <Check size={14} /> : <Plus size={14} />}
                  {inCompare ? t.common.remove : t.common.addToCompare}
                </button>
              </div>
            </div>

            <aside className="w-full md:w-72 bg-white border border-[#E2E8F0] p-6 space-y-4 text-sm">
              <div>
                <div className="overline mb-2">{t.common.pricing}</div>
                <SidebarPrice app={app} lang={lang} fxRates={fxRates} freeLabel={t.common.free} fromLabel={lang === "nl" ? "Vanaf" : "From"} />
              </div>
              <SimilarPricedStrip slug={app.slug} lang={lang} />
              <div className="border-t border-[#E2E8F0] pt-4">
                <div className="overline mb-2">{t.common.platforms}</div>
                <div className="flex items-center gap-3 text-[#64748B]">
                  {app.platforms?.ios && (
                    <span className="inline-flex items-center gap-1">
                      <Smartphone size={14} /> iOS
                    </span>
                  )}
                  {app.platforms?.android && (
                    <span className="inline-flex items-center gap-1">
                      <Smartphone size={14} /> Android
                    </span>
                  )}
                  {app.platforms?.web && (
                    <span className="inline-flex items-center gap-1">
                      <Monitor size={14} /> Web
                    </span>
                  )}
                </div>
              </div>
              <div className="border-t border-[#E2E8F0] pt-4">
                <div className="overline mb-2">{t.common.languages}</div>
                <div className="text-[#64748B] font-mono text-xs">
                  {(app.languages || []).join(" · ")}
                </div>
              </div>
              <div className="border-t border-[#E2E8F0] pt-4">
                <div className="overline mb-2">{t.common.audience}</div>
                <div className="flex flex-wrap gap-1">
                  {(app.target_audience || []).map((a) => (
                    <span key={a} className="hac-chip">
                      {t.audiences[a] || a}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <AppGallery slug={app.slug} />

      {Array.isArray(app.plans) && app.plans.length > 0 && (
        <section id="plans" className="bg-[#F8FAFC] border-y border-[#E2E8F0] scroll-mt-20" data-testid="app-plans-section">
          <div className="hac-container py-16">
            <div className="max-w-2xl mb-10">
              <div className="overline mb-3">{lang === "nl" ? "Plannen & modules" : "Plans & modules"}</div>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] leading-tight mb-3">
                {lang === "nl" ? `${app.name} prijsplannen` : `${app.name} pricing tiers`}
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                {lang === "nl"
                  ? "Vergelijk welke modules in elk plan zitten. Prijzen worden door de app-eigenaar zelf bijgehouden."
                  : "Compare which modules are included in each tier. Prices are kept up-to-date by the app owner."}
              </p>
            </div>
            <div className={`grid gap-4 ${app.plans.length === 1 ? "md:grid-cols-1 max-w-sm" : app.plans.length === 2 ? "md:grid-cols-2 max-w-3xl" : app.plans.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"}`}>
              {app.plans.map((p, i) => {
                const { primary, secondary, isFree } = formatPlanPrice(p, lang);
                const approx = isFree ? [] : planApproximations(p, fxRates, lang);
                return (
                  <div
                    key={`${p.name}-${i}`}
                    className={`bg-white rounded-2xl p-6 flex flex-col relative ${p.popular ? "border-2 border-amber-400 shadow-md" : "border border-[#E2E8F0]"}`}
                    data-testid={`plan-card-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {p.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full inline-flex items-center gap-1">
                        <Star size={10} className="fill-white" />
                        {lang === "nl" ? "Meest gekozen" : "Most popular"}
                      </span>
                    )}
                    <div className="font-heading text-xl text-[#0F172A] mb-1">{p.name}</div>
                    {p.description && <p className="text-xs text-[#64748B] mb-4">{p.description}</p>}
                    <div className="mb-5">
                      {isFree ? (
                        <div className="font-heading text-3xl text-[#0F172A]">{primary}</div>
                      ) : (
                        <>
                          <div className="font-heading text-2xl md:text-3xl text-[#0F172A] leading-tight break-words">{primary}</div>
                          {secondary && (
                            <div className="text-xs text-[#64748B] mt-1 font-mono">{secondary}</div>
                          )}
                          {approx.length > 0 && (
                            <div
                              className="text-[11px] text-[#94A3B8] mt-1.5 font-mono"
                              title={lang === "nl" ? "Indicatieve omrekening — koers via ECB" : "Approximate — daily ECB rate"}
                              data-testid={`plan-approx-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                              {approx.join(" · ")}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {(p.features || []).length > 0 && (
                      <ul className="space-y-2 text-sm flex-1">
                        {p.features.map((f, fi) => (
                          <li key={fi} className="flex items-start gap-2 text-[#0F172A]">
                            <Check size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <FeatureSpotlight app={app} />

      <AppReviews slug={app.slug} lang={lang} />

      <AdSlot placement="directory-banner" />

      <section className="hac-container py-16">
        <div className="grid md:grid-cols-3 gap-12">
          <div className="md:col-span-2 space-y-12">
            <div>
              <div className="overline mb-3">{t.common.overview}</div>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4 leading-tight">
                {app.name} — {tagline}
              </h2>
              <MarkdownView text={description} className="leading-relaxed" />
            </div>

            <div>
              <div className="overline mb-3">{t.common.featuresLabel}</div>
              <h3 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-6">
                {featureSet.size} {t.common.featuresLabel}
              </h3>
              <div className="space-y-8">
                {Object.entries(categories).map(([cat, feats]) => {
                  const supported = feats.filter((f) => featureSet.has(f));
                  if (supported.length === 0) return null;
                  return (
                    <div key={cat}>
                      <h4 className="font-heading text-xl text-[#2563EB] mb-3">
                        {t.categories[cat]}
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {feats.map((f) => {
                          const has = featureSet.has(f);
                          return (
                            <div
                              key={f}
                              className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg ${
                                has
                                  ? "bg-[#EFF6FF] border-[#DBEAFE] text-[#0F172A]"
                                  : "border-transparent text-[#94A3B8]"
                              }`}
                            >
                              {has ? (
                                <Check size={14} className="text-[#2563EB] shrink-0" />
                              ) : (
                                <span className="w-3.5 h-3.5 border border-[#E2E8F0] shrink-0 rounded-sm" />
                              )}
                              <span className={has ? "" : "line-through"}>
                                {t.features[f] || f}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-6">
              <ShieldCheck size={20} className="text-[#2563EB] mb-3" />
              <div className="overline mb-2">{t.common.score}</div>
              <div className="font-heading text-5xl text-[#0F172A]">
                {Math.round(app.score || 0)}
              </div>
              <div className="text-xs text-[#64748B] mt-2">
                {t.sections.rankingSub}
              </div>
            </div>
            <Link
              to="/advisor"
              className="block bg-white border border-[#E2E8F0] rounded-xl p-6 hover:border-[#2563EB] transition-colors"
            >
              <Users size={20} className="text-[#2563EB] mb-3" />
              <div className="font-heading text-xl text-[#0F172A] mb-1">
                {t.sections.advisor}
              </div>
              <p className="text-xs text-[#64748B]">{t.sections.advisorSub}</p>
            </Link>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default AppDetailPage;
